import { produce } from "immer";
import _ from "lodash";
import { mergeWithArrays } from "src/util/functions";
import { tVariantOption } from "tmt_api/src/types/types";
import { tVariant } from "tmt_api/src/types/types.validator";
import { kDefaultProduct, kDefaultVariantOption } from "./defaults";
import {
  tDeleteVariantOptionArray,
  tFillProduct,
  tProductAction,
  tProductState,
  tSetProduct,
  tUpdateVariantArray,
  tUpdateVariantOptionArray,
} from "./types";

export const _getVariantArray = (
  myVariantOptions: Array<tVariantOption>,
  myOldVariants: Array<tVariant>,
  myCurrentState: tProductState,
): Array<tVariant> => {
  if (myVariantOptions.length === 0) {
    return [];
  }
  const aAllOptions: Array<Array<{ name: string; label: string }>> = myVariantOptions.map((varOpt) => {
    const aTempVarFlat: Array<{ name: string; label: string }> = [];
    varOpt.labels.forEach((label) => {
      aTempVarFlat.push({ name: varOpt.name, label: label });
    });
    return aTempVarFlat;
  });
  const aTempVariantOptionArray: Array<Array<{ name: string; label: string }>> = [],
    aMax = aAllOptions.length - 1;

  const getAllVariantOptions = (myArr: Array<{ name: string; label: string }>, aVariantOptionInd: number) => {
    for (
      let aVariantValueInd = 0, aLength = aAllOptions[aVariantOptionInd].length;
      aVariantValueInd < aLength;
      aVariantValueInd++
    ) {
      const a = myArr.slice(0); // clone arr
      a.push(aAllOptions[aVariantOptionInd][aVariantValueInd]);
      if (aVariantOptionInd === aMax) aTempVariantOptionArray.push(a);
      else getAllVariantOptions(a, aVariantOptionInd + 1);
    }
  };

  getAllVariantOptions([], 0); // todo:  why is this here?

  const aTempVariants: Array<tVariant> = aTempVariantOptionArray.map((val) => {
    if (myOldVariants.filter((oldVal) => JSON.stringify(oldVal.options) === JSON.stringify(val))[0]) {
      return { ...myOldVariants.filter((oldVal) => JSON.stringify(oldVal.options) === JSON.stringify(val))[0] };
    }
    return {
      inventory: myCurrentState.inventory ?? 0,
      options: [...val],
      msrpPrice: myCurrentState.msrp_price ?? 0,
      sku: `${myCurrentState.sku !== "" ? myCurrentState.sku : "TEST"}-${val
        .map((opt) => opt.label.replace(/\s+/g, "-").toUpperCase())
        .join("-")}`,
      wholesalePrice: myCurrentState.wholesale_price ?? 0,
      purchasable: true,
    };
  });
  return aTempVariants;
};

export const handleSetProduct = (
  state: tProductState = kDefaultProduct,
  { productPart }: tSetProduct,
): tProductState => {
  const newState = mergeWithArrays<tProductState>(state, "REPLACE", productPart);
  if (Object.prototype.hasOwnProperty.call(productPart, "tester") && productPart.tester === undefined) {
    newState.tester = undefined;
  }
  newState.hasUnsavedChanges = true;
  return newState;
};

export const handleFillProduct = (action: tFillProduct): tProductState => {
  return action.product;
};

export const handleUpdateVariants = (
  state: tProductState = kDefaultProduct,
  { index, singleVariantPart }: tUpdateVariantArray,
): tProductState => {
  state.variants.forEach((myVariant, myIndex) => {
    if (myIndex === index) {
      _.merge<tVariant, tUpdateVariantArray["singleVariantPart"]>(myVariant, singleVariantPart);
      if (Object.prototype.hasOwnProperty.call(singleVariantPart, "tester") && singleVariantPart.tester === undefined) {
        myVariant.tester = undefined;
      }
    }
  });
  state.hasUnsavedChanges = true;
  return state;
};

export const handleUpdateVariantOptions = (
  state: tProductState = kDefaultProduct,
  { index, singleVariantOptionPart }: tUpdateVariantOptionArray,
): tProductState => {
  if (index === state.variantOptions.length) {
    state.variantOptions.push(kDefaultVariantOption);
    state.hasUnsavedChanges = true;
    return state;
  }

  state.variantOptions.forEach((myVariant, myIndex) => {
    if (myIndex === index) {
      if (singleVariantOptionPart.labels !== undefined) {
        state.variantOptions[myIndex].labels = singleVariantOptionPart.labels;
      } else {
        _.merge<tVariantOption, tUpdateVariantOptionArray["singleVariantOptionPart"]>(
          myVariant,
          singleVariantOptionPart,
        );
      }
      state.variants = _getVariantArray(state.variantOptions, state.variants, state);
    }
  });

  state.hasUnsavedChanges = true;

  return state;
};

export const handleDeleteVariantOption = (
  state: tProductState = kDefaultProduct,
  { index }: tDeleteVariantOptionArray,
): tProductState => {
  state.variantOptions.splice(index, 1);
  state.variants = _getVariantArray(state.variantOptions, state.variants, state);
  state.hasUnsavedChanges = true;
  return state;
};

export const ProductReducer = produce(
  (draft: tProductState = kDefaultProduct, action: tProductAction): tProductState => {
    switch (action.type) {
      case "SET_PRODUCT":
        return handleSetProduct(draft, action);
      case "FILL_PRODUCT":
        return handleFillProduct(action);
      case "UPDATE_VARIANT_ARRAY":
        return handleUpdateVariants(draft, action);
      case "UPDATE_VARIANT_OPTION_ARRAY":
        return handleUpdateVariantOptions(draft, action);
      case "DELETE_VARIANT_OPTION":
        return handleDeleteVariantOption(draft, action);
      case "SET_SUBMITTING":
        draft.isSubmitting = action.payload;
        return draft;
      case "RESET_FORM_STATE":
        draft.isSubmitting = false;
        draft.hasUnsavedChanges = false;
        return draft;
      default:
        return draft;
    }
  },
);
